import { format, parseISO } from 'date-fns';
import { useRef } from 'react';
import cs from 'classnames';
import { ARTICLE_URL } from '@constants';
import { Article, ArticleVariant } from '@repo/types';
import { Link } from 'react-router';
import { ARTICLE_PUBLISHED_DATE_FORMAT } from '../../constants';
import { Image, ImageVariant } from '../../../common/components/Image';
import { useCropGeometry, buildCropStyle } from '../../../utils/domMeasuredCrop';
import { formatTag } from '../../../utils/formatTag';
import { cardSizeClass } from '../../../utils/cardSizeClass';
import { ArticleHighlightedCardProps } from './contracts';

export type ArticleHighlightedCardUIProps = Pick<
  ArticleHighlightedCardProps,
  'variant' | 'style' | 'showAuthor' | 'showDate' | 'showImage' | 'showTag' | 'prioritize' | 'imageObjectFit' | 'size'
> & {
  article: Article;
};

function ArticleHighlightedCardUI({
  article,
  variant,
  style,
  showAuthor,
  showDate,
  showImage,
  showTag,
  prioritize,
  imageObjectFit = 'cover',
  size,
}: ArticleHighlightedCardUIProps) {
  const { title, imageSrc, author, slug, publishDate, summary, tags } = article;
  const publishedDate = publishDate ? format(parseISO(publishDate), ARTICLE_PUBLISHED_DATE_FORMAT) : '';

  const wrapperRef = useRef<HTMLDivElement>(null);
  // The CMS grid stretches this card to fill its cell (slc-column--fit-to-siblings),
  // so the media frame has no fixed aspect ratio — its height tracks the row. Use
  // `cover` so the image fills that frame; `contain` would centre the image at its
  // own aspect ratio and leave large gaps of the gradient background above/below.
  const cropGeometry = useCropGeometry(wrapperRef, '.article-highlighted-card__thumbnail', article.imageCropData);
  const cropStyle = cropGeometry ? buildCropStyle(cropGeometry, { fit: 'cover', position: 'absolute' }) : undefined;

  const sizeClass = cardSizeClass('article-highlighted-card', size);
  const className = cs(
    'article-highlighted-card',
    {
      'article-highlighted-card--no-image': !showImage,
      'article-highlighted-card--no-tag': !showTag,
      'article-highlighted-card--no-date': !showDate,
      'article-highlighted-card--default': variant === ArticleVariant.Default,
      'article-highlighted-card--stacked': variant === ArticleVariant.Stacked,
      'article-highlighted-card--text-side-by-side': variant === ArticleVariant.TextSideBySide,
    },
    sizeClass
  );

  const firstTag = tags?.[0];

  return (
    <article style={style} className={className}>
      <Link className="article-highlighted-card__link-wrapper" to={`${ARTICLE_URL}/${slug}`}>
        {imageSrc && showImage && (
          <div ref={wrapperRef} className="article-highlighted-card__media">
            <Image
              className="article-highlighted-card__thumbnail"
              variant={ImageVariant.HeroS}
              src={imageSrc}
              alt={title}
              width={550}
              height={350}
              style={cropStyle ?? { objectFit: imageObjectFit }}
              priority={prioritize}
              fallbackToLogo
              sizes="(max-width: 767px) calc(100vw - 32px), 550px"
            />
            {showTag && firstTag && (
              <span className="article-highlighted-card__tag text-3xs">{formatTag(firstTag)}</span>
            )}
          </div>
        )}
        {(!imageSrc || !showImage) && (
          <div className="article-highlighted-card__media article-highlighted-card__media--no-image" />
        )}
        <div className="article-highlighted-card__body">
          {(showDate || (author && showAuthor)) && (
            <div className="article-highlighted-card__meta">
              {showDate && (
                <time className="article-highlighted-card__date text-3xs" dateTime={article.publishDate}>
                  {publishedDate}
                </time>
              )}
              {author && showAuthor && (
                <p className="article-highlighted-card__author text-3xs">
                  <span className="article-highlighted-card__author-label">by </span>
                  <span className="article-highlighted-card__author-value">{author}</span>
                </p>
              )}
            </div>
          )}
          <h3 className="article-highlighted-card__title">{title}</h3>
          {summary && <p className="article-highlighted-card__excerpt small-text">{summary}</p>}
          {/* Visual CTA matching the design. The whole card is already the
              link, so this is a styled span (valid inside the anchor) rather
              than a nested interactive element. */}
          <span className="article-highlighted-card__cta text-3xs">Read story</span>
        </div>
      </Link>
    </article>
  );
}

export default ArticleHighlightedCardUI;
